<script setup>
const props = defineProps({
    checkboxConfig: {
        type: Object,
        required: false,
        default: () => ({}),
        validator(rawValue) {
            const validations = {
                name: rawValue => typeof rawValue?.name === 'string',
                uid: rawValue => typeof rawValue?.uid === 'string',
                locale: rawValue => rawValue?.locale === undefined || typeof rawValue?.locale === 'string',
                required: rawValue => rawValue?.required === undefined || typeof rawValue?.required === 'string',
                maxDate: rawValue => rawValue?.maxDate === undefined || typeof rawValue?.maxDate === 'object',
                placeholder: rawValue => rawValue?.placeholder === undefined || typeof rawValue?.placeholder === 'string',
                timePicker: rawValue => rawValue?.timePicker === undefined || typeof rawValue?.timePicker === 'boolean',
                value: rawValue => rawValue?.value === undefined || typeof rawValue?.value === 'string',
                state: rawValue => rawValue?.state === undefined || typeof rawValue?.state === 'boolean',
                startValidation: rawValue => rawValue?.startValidation === undefined || typeof rawValue?.startValidation === 'boolean',
                errorMessage: rawValue => rawValue?.errorMessage === undefined || typeof rawValue?.errorMessage === 'string',
            }

            return Object.values(validations).every(validation => validation(rawValue))
        }
    }
})

//  Creates a form field with its validation state, you will use this inside your custom input components.
const { value, errorMessage, meta, resetField } = useField(() =>
    props.checkboxConfig?.name, props.checkboxConfig?.validationRules
)

// Show error + success classes in case fields have validation rules
const requiresValidation = computed(() => props.checkboxConfig?.validationRules !== undefined)

const formFieldClass = computed(() => {
    return {
        'form-error': !meta.valid && requiresValidation.value && errorMessage.value
    }
})

// Change initial value with dynamic data of input field
resetField({
    value: props.checkboxConfig?.val ? props.checkboxConfig?.val : []
})

// Use watcher to update data with dynamic values (seems necessary...?)
watch(() => props.checkboxConfig?.val, () => {
    resetField({
        value: props.checkboxConfig?.val ? props.checkboxConfig?.val : []
    })
})

const toggleCheckbox = () => {
    if (value.value.length > 0) {
        value.value = []
    } else {
        value.value = [props.checkboxConfig?.value]
    }
}
</script>)

<template>
    <div :class="formFieldClass" class="form-input-checkbox-wrapper">
        <input
            :id="checkboxConfig.uid"
            v-model="value"
            class="form-field-checkbox"
            type="checkbox"
            :required="checkboxConfig.required"
            :name="checkboxConfig.name"
            :value="checkboxConfig.value"
            tabindex="-1"
        >
        <label
            class="form-field-checkbox-label-wrapper"
            :for="checkboxConfig.uid"
            tabindex="0"
            @keydown.space.prevent="toggleCheckbox"
            @keydown.enter.prevent="toggleCheckbox"
        >
            <span class="form-field-checkbox-label" :class="checkboxConfig.required">
                <span v-html="checkboxConfig.label" />

                <NuxtLinkLocale v-if="checkboxConfig.link" :to="{ name: 'dataProtection' }" class="link">
                    {{ checkboxConfig.link }}
                </NuxtLinkLocale>
            </span>
        </label>

        <FormFieldMessage
            :msg-config="{
                message: errorMessage,
                error: errorMessage && ! meta.valid
            }"
        />
    </div>
</template>

<style lang="scss">
.form-field-checkbox {
    @include form-field-checkbox;
}

.form-field-checkbox-label {
    @include form-field-checkbox-label;
}

.form-field-checkbox-label-wrapper {
    @include form-field-checkbox-label-wrapper;
}

.form-input-checkbox-wrapper {
    @include checkbox-wrapper;
}
</style>
